import { ChevronRight, Logout, PlayArrow } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useProfileNoScale, useTrustCenterSections } from '../../api';
import AppBar from '../../app-bar/AppBar';
import defaultSimulator from '../../assets/default-simulator.svg';
import profileSkeleton from '../../assets/profile-skeleton.svg';
import { useAuth } from '../../auth';
import Avatar from '../../avatar/Avatar';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import TypeChip from '../../chip/TypeChip';
import MdViewer from '../../markdown/MdViewer';
import { SimulatorType } from '../../types';
import ProfileInfo from './components/ProfileInfo';
import ProfileSettings from './components/ProfileSettings';

const LinkCard = ({ content, title, to }: { content: string; title: string; to: string }) => (
  <Card>
    <CardActionArea component={RouterLink} to={to}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" mb={1}>
              {title}
            </Typography>

            <MdViewer text={content}></MdViewer>
          </Box>
          <ChevronRight sx={theme => ({ color: theme.palette.text.secondary })} />
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const ProfilePage = () => {
  const { profile, loadingProfile, profileError } = useProfileNoScale();
  const { trustCenterSections } = useTrustCenterSections();
  const { logoutRedirect } = useAuth();
  const [showSimulators, setShowSimulators] = useState(false);

  return (
    <>
      <AppBar title="Profile" />
      <Breadcrumbs
        items={[{ label: 'Home', to: '/' }, { label: 'Profile' }]}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />
      <Container sx={{ my: 4, textAlign: 'center' }}>
        {loadingProfile ? (
          <Box
            alignItems="center"
            display="flex"
            flexDirection={{ xs: 'column', sm: 'column-reverse' }}
            gap={{ xs: 3, sm: 6 }}
          >
            <div>
              <Skeleton height={36} width={302}></Skeleton>
              <Skeleton height={24} width={302}></Skeleton>
            </div>
            <img src={profileSkeleton} />
          </Box>
        ) : profileError ? (
          <p>Error loading profile!</p>
        ) : (
          profile && (
            <ProfileInfo
              avatar={profile.avatar}
              company={profile.companies[0]}
              joinedDate={new Date(profile.joinedDate)}
              name={profile.name}
              phone={profile.phone}
              email={profile.email}
            />
          )
        )}

        <Box alignItems="center" display="flex" flexDirection="column">
          <Box display="flex" gap={1} mb={2} mt={2}>
            {profile?.simulators?.length ? (
              <Button onClick={() => setShowSimulators(true)} variant="outlined" startIcon={<PlayArrow />}>
                Simulator access
              </Button>
            ) : null}
            <Button onClick={() => logoutRedirect()} variant="outlined" startIcon={<Logout />}>
              Sign out
            </Button>
          </Box>
        </Box>
        {loadingProfile ? (
          <Skeleton height={350} variant="rounded" sx={{ mb: 8 }} />
        ) : (
          profile?.companies[0] && <ProfileSettings company={profile.companies[0]} />
        )}
        <Box textAlign="left">
          <Typography variant="h6" gutterBottom>
            Legal
          </Typography>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            Moving into the cloud means handing over the care of one of your most prized assets – your data – to us. We
            understand that you must be able to trust that your data is safe with us and that our services comply with
            applicable laws and regulations.
            <br />
            Below is information on what we do to safeguard your data, what we do to ensure compliance and how we
            deliver the high quality services offered on K-Sim Connect. The legal framework for K-Sim Connect and
            associated services is contained in the links below.
          </Typography>

          {trustCenterSections && (
            <Box display="flex" flexDirection="column" gap={2}>
              {trustCenterSections.map(({ description, slug, title }) => (
                <LinkCard key={title} content={description} title={title} to={`/${slug}`} />
              ))}
            </Box>
          )}
        </Box>

        <Dialog open={showSimulators} fullWidth maxWidth="xl">
          <DialogTitle>Simulation access</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { md: 'repeat(2, 1fr)' }, gap: 1 }}>
              {profile?.simulators?.map(simulator => (
                <Card key={simulator.id}>
                  <CardContent sx={{ display: 'flex', gap: 1, height: '100%' }}>
                    <Box flex="1" display="flex" flexDirection="column">
                      <Box sx={{ justifyContent: 'space-between', gap: 1, display: 'flex' }}>
                        <Avatar
                          src={
                            simulator.type === SimulatorType.Site
                              ? defaultSimulator
                              : simulator?.thumbnail || defaultSimulator
                          }
                          sx={{ height: 60, width: 60 }}
                        />
                        <Typography variant="overline" sx={{ lineHeight: { xs: '32px', sm: '60px' } }}>
                          {simulator.name}
                        </Typography>
                        <Box flex="1"></Box>
                        <TypeChip type={simulator.type} />
                        <TypeChip type={simulator.platform} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSimulators(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ProfilePage;
